// routes
import { PATH_DASHBOARD, PATH_PAGE } from '../../../routes/paths';
// components
import SvgColor from '../../../components/svg-color';
import { Configuration } from '../../../@types/configuration';
import { DynNavConfig } from '../../../components/nav-section';
import { AuthUserType } from '../../../auth/types';
import { userHasRole } from '../../../auth/utils';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  blog: icon('ic_blog'),
  cart: icon('ic_cart'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  map: icon('ic_map'),
  tour: icon('ic_tour'),
  tourPlanned: icon('ic_tour_planned'),
};

export function createNavConfig(data: Configuration, user: AuthUserType) {
  const dynNavConfig: DynNavConfig = [
    {
      subheader: '',
      items: [
        {
          title: 'globalMap.title',
          path: PATH_PAGE.map,
          icon: ICONS.map,
          roles: ['school-read'],
        },
      ],
    },
    {
      subheader: '',
      roles: ['tour-read'],
      items: [
        {
          title: 'tours.plural',
          path: PATH_DASHBOARD.tour.root,
          icon: ICONS.tour,
          roles: ['tour-read'],
        },
        {
          title: 'tours.plan.plural',
          path: PATH_DASHBOARD.tourPlan.root,
          icon: ICONS.tourPlanned,
          roles: ['tour-read'],
        },
      ],
    },
    {
      subheader: '',
      roles: ['school-read'],
      items: [
        {
          title: 'student.plural',
          path: PATH_DASHBOARD.student.root,
          icon: ICONS.user,
          roles: ['school-read'],
        },
        {
          title: 'school.plural',
          path: PATH_DASHBOARD.school.root,
          icon: ICONS.banking,
          roles: ['school-read'],
          children: [
            { title: 'school.plural', path: PATH_DASHBOARD.school.list },
            { title: 'schoolcenter.plural', path: PATH_DASHBOARD.schoolCenter.list },
          ],
        },
      ],
    },
    {
      subheader: 'exportPage.navSubheader',
      roles: ['export-read', 'tour-tender'],
      items: [
        {
          title: 'exportPage.title',
          path: PATH_DASHBOARD.globalExport.root,
          roles: ['export-read'],
        },
        {
          title: 'tours.tender.title',
          path: PATH_DASHBOARD.tourTender.root,
          roles: ['tour-tender'],
        },
      ],
    },
    {
      subheader: 'transportationCompany.plural',
      roles: ['transport-read'],
      items: [
        {
          title: 'transportationCompany.plural',
          path: PATH_DASHBOARD.transportationCompany.root,
          roles: ['transport-read'],
        },
        {
          title: 'employee.gender_MALE',
          path: PATH_DASHBOARD.employee.root,
          roles: ['transport-read'],
        },
        {
          title: 'vehicle.plural',
          path: PATH_DASHBOARD.vehicle.root,
          roles: ['transport-read'],
        },
      ],
    },
    {
      subheader: 'application.titlePlural',
      roles: ['studentApplicationForm-read'],
      items: [
        {
          title: 'studentApplication.title',
          path: PATH_DASHBOARD.studentApplication.root,
          roles: ['studentApplicationForm-read'],
        },
      ],
    },
    {
      subheader: 'basicData.plural',
      roles: ['config-write', 'school-write', 'transport-write'],
      items: [
        {
          title: 'placeManagement.title',
          path: PATH_DASHBOARD.basicSetting.placeManagement,
        },
        {
          title: 'basicData.plural',
          path: '',
          children: [
            {
              title: 'config.timeMatrix_config',
              path: PATH_DASHBOARD.basicSetting.timeMatrix,
            },
            {
              title: 'vacation.template.plural',
              path: PATH_DASHBOARD.basicSetting.schoolVacationTemplate.list,
            },
            {
              title: 'lot.title',
              path: PATH_DASHBOARD.basicSetting.lotConfig,
            },
            {
              title: 'transportationPeriod.plural',
              path: PATH_DASHBOARD.basicSetting.transportationPeriodConfig.list,
            },
            {
              title: 'school.config',
              path: PATH_DASHBOARD.basicSetting.schoolConfig,
            },
            ...(data.roadblocksEnabled
              ? [
                  {
                    title: 'roadblockManagement.title',
                    path: PATH_DASHBOARD.basicSetting.roadblockManagement,
                  },
                ]
              : []),
            {
              title: 'tourBundle.bundleName',
              path: PATH_DASHBOARD.basicSetting.tourBundle.list,
            },
          ],
        },
        {
          title: 'excelImport.plural',
          path: PATH_DASHBOARD.basicSetting.excelImport.root,
          roles: ['config-write', 'school-write', 'transport-write'],
          children: [
            {
              title: 'student.plural',
              path: PATH_DASHBOARD.basicSetting.excelImport.student,
            },
            {
              title: 'employee.gender_MALE',
              path: PATH_DASHBOARD.basicSetting.excelImport.employee,
            },
            {
              title: 'vehicle.plural',
              path: PATH_DASHBOARD.basicSetting.excelImport.vehicle,
            },
          ],
        },
        {
          title: 'analysis.singular',
          path: PATH_DASHBOARD.root,
          roles: ['tour-write'],
        },
      ],
    },
  ];

  if (data.supportEmployeeApp) {
    dynNavConfig.splice(4, 0, {
      subheader: 'employeeApp.title',
      roles: ['employeeAppDispo-read', 'employeeAppDispo-write', 'employeeAppDispo-export'],
      items: [
        {
          title: 'employeeApp.messages.plural',
          path: PATH_DASHBOARD.employeeApp.employeeMessage,
          roles: ['employeeAppDispo-read', 'employeeAppDispo-write'],
        },
        {
          title: 'employeeApp.export.singular',
          path: PATH_DASHBOARD.employeeApp.employeeAppExport,
          roles: ['employeeAppDispo-export'],
        },
        {
          title: 'employeeApp.comparison.title',
          path: PATH_DASHBOARD.employeeApp.employeeAppTrackComparison,
          roles: ['employeeAppDispo-export'],
        },
      ],
    });
  }

  if (data.moduleInterkommunalerLastenausgleichEnabled) {
    const exportIndex = dynNavConfig.findIndex(
      (elem) => elem.subheader === 'exportPage.navSubheader'
    );

    if (exportIndex !== -1) {
      dynNavConfig[exportIndex].items.push({
        title: 'interkommunalerLastenausgleich.title',
        path: PATH_DASHBOARD.interkommunalerLastenausgleich.root,
      });
    }
  }

  if (userHasRole(user, 'superuser')) {
    // if superuser, add public-transportation to excel-import
    const basicDataIndex = dynNavConfig.findIndex((elem) => elem.subheader === 'basicData.plural');
    const excelImportIndex = dynNavConfig[basicDataIndex].items.findIndex(
      (elem) => elem.title === 'excelImport.plural'
    );

    dynNavConfig[basicDataIndex].items![excelImportIndex].children!.push({
      title: 'publicTransportationStations',
      path: PATH_DASHBOARD.basicSetting.excelImport.publicTransportation,
    });
  }

  return dynNavConfig;
}
